import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import studyDenLogo from '../assets/images/branding/Studyden_Horizontal.svg';
import studyDenFooterLogo from '../assets/images/branding/Studyden_White_Wordmark_Transparent.svg';
import './LandingPage.css';

/* ---------- Inline SVG icons ---------- */
const Icon = {
  Check: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  Document: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" />
      <path d="M8 13h8M8 17h5" />
    </svg>
  ),
  Chart: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18" />
      <path d="M7 14l4-4 4 4 5-6" />
    </svg>
  ),
  Clock: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7v5l3 2" />
    </svg>
  ),
  Star: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="m12 2.8 2.75 5.57 6.15.9-4.45 4.33 1.05 6.12L12 16.83l-5.5 2.89 1.05-6.12L3.1 9.27l6.15-.9L12 2.8z" />
    </svg>
  ),
  Users: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  Assessment: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 5h6" />
      <path d="M9 3h6a2 2 0 0 1 2 2v1H7V5a2 2 0 0 1 2-2z" />
      <path d="M7 5H5a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
      <path d="m8 14 2.5 2.5L16 11" />
    </svg>
  ),
  Shield: () => (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ),
  Arrow: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 12h14M13 5l7 7-7 7" />
    </svg>
  ),
  Quote: () => (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="currentColor">
      <path d="M9 7H5a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2v3a2 2 0 0 1-2 2H4v2h1a4 4 0 0 0 4-4V7zM20 7h-4a2 2 0 0 0-2 2v4a2 2 0 0 0 2 2h2v3a2 2 0 0 1-2 2h-1v2h1a4 4 0 0 0 4-4V7z" />
    </svg>
  ),
  Menu: () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="3" y1="6" x2="21" y2="6" />
      <line x1="3" y1="12" x2="21" y2="12" />
      <line x1="3" y1="18" x2="21" y2="18" />
    </svg>
  ),
};

/* ---------- Hero mockup ---------- */
const HeroMockup = () => (
  <div className="hero-mockup" aria-hidden="true">
    <div className="mockup-window">
      <div className="mockup-window__bar">
        <span className="mockup-dot mockup-dot--red" />
        <span className="mockup-dot mockup-dot--yellow" />
        <span className="mockup-dot mockup-dot--green" />
        <div className="mockup-window__url">thestudyden.com / assessment</div>
      </div>

      <div className="mockup-window__body">
        <div className="mockup-row">
          <div className="mockup-question">
            <div className="mockup-label">Question 1</div>
            <p>Explain how AI can support teachers in modern classrooms.</p>
          </div>
          <div className="mockup-score">
            <svg viewBox="0 0 36 36" className="mockup-score__ring">
              <path
                className="mockup-score__bg"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
              <path
                className="mockup-score__fill"
                strokeDasharray="65, 100"
                d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
              />
            </svg>
            <div className="mockup-score__text">
              <strong>65</strong>
              <span>/100</span>
            </div>
          </div>
        </div>

        <div className="mockup-answer">
          <div className="mockup-label">Student response</div>
          <p>
            <span className="hl hl--green">AI can save teachers time</span> by automatically marking
            answers and giving feedback. <span className="hl hl--amber">It also helps</span> students
            understand what they got wrong faster.
          </p>
        </div>

        <div className="mockup-feedback">
          <div className="mockup-feedback__icon"><Icon.Assessment /></div>
          <div>
            <div className="mockup-feedback__title">AI Feedback</div>
            <div className="mockup-feedback__text">
              Strong opening — try linking AI to specific tasks like grading or feedback for full marks.
            </div>
          </div>
        </div>

        <div className="mockup-tags">
          <span className="mockup-tag">✓ Clear writing</span>
          <span className="mockup-tag">✓ On-topic</span>
          <span className="mockup-tag mockup-tag--amber">! Add example</span>
        </div>
      </div>
    </div>
  </div>
);

const ROTATE_MS = 6000;

const LandingPage = () => {
  const navigate = useNavigate();
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const [shouldFadeInHero, setShouldFadeInHero] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const autoRotateRef = useRef(null);

  const testimonials = useMemo(() => [
    {
      quote: "One of the most impressive features of StudyDen's AI marking is its speed. Within just a few minutes the AI generated a comprehensive assessment — a game-changer in an online learning environment where timely evaluations are crucial.",
      name: "Stepan", role: "Student", initial: "S",
    },
    {
      quote: "You worked very hard on this project, and I'm sure it's going to help a lot of students. I see this being a very successful project — well done.",
      name: "Anthony", role: "Student", initial: "A",
    },
    {
      quote: "Amazing website! It's fast, clean and the feedback is genuinely useful.",
      name: "Aditya", role: "Student", initial: "A",
    },
    {
      quote: "StudyDen is very good. The interface makes reviewing answers really easy.",
      name: "Leo", role: "Student", initial: "L",
    },
  ], []);

  const features = useMemo(() => [
    {
      icon: <Icon.Document />,
      title: 'Learn',
      text: 'Bring learning content and class resources together so learners can access what they need in one clear, connected place.',
    },
    {
      icon: <Icon.Assessment />,
      title: 'Assess',
      text: 'Create and assign assessments with intelligent tools, flexible class workflows and support for learner accessibility needs.',
    },
    {
      icon: <Icon.Chart />,
      title: 'Progress',
      text: 'Use AI-powered marking, meaningful feedback and performance insights to understand progress and guide improvement.',
    },
  ], []);

  const stats = useMemo(() => [
    { value: '95%',  label: 'rated their experience as excellent' },
    { value: '90%',  label: 'would recommend to their peers' },
    { value: '100%', label: 'found feedback detailed and helpful' },
  ], []);

  /* ---------- Auto-rotate testimonials ---------- */
  const startAutoRotate = useCallback(() => {
    if (autoRotateRef.current) clearInterval(autoRotateRef.current);
    autoRotateRef.current = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, ROTATE_MS);
  }, [testimonials.length]);

  useEffect(() => {
    startAutoRotate();
    return () => { if (autoRotateRef.current) clearInterval(autoRotateRef.current); };
  }, [startAutoRotate]);

  useEffect(() => {
    if (currentTestimonial >= testimonials.length) setCurrentTestimonial(0);
  }, [currentTestimonial, testimonials.length]);

  const nextTestimonial = useCallback(() => {
    setCurrentTestimonial((p) => (p + 1) % testimonials.length);
    startAutoRotate();
  }, [startAutoRotate, testimonials.length]);

  const prevTestimonial = useCallback(() => {
    setCurrentTestimonial((p) => (p - 1 + testimonials.length) % testimonials.length);
    startAutoRotate();
  }, [startAutoRotate, testimonials.length]);

  const toggleMobileMenu = useCallback(() => setShowMobileMenu((p) => !p), []);

  /* ---------- Entrance / scroll behaviour ---------- */
  // Startup loading belongs to App, which renders this page only once auth/profile
  // state is ready. The page must not mount a second loading screen of its own.
  useEffect(() => {
    window.scrollTo(0, 0);
    const t = setTimeout(() => setShouldFadeInHero(true), 100);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToId = useCallback((id) => (e) => {
    if (e) e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setShowMobileMenu(false);
  }, []);

  const goToContact = scrollToId('contact-section');

  return (
    <div className="lp">
      {/* Background decoration */}
      <div className="lp-bg" aria-hidden="true">
        <div className="lp-bg__blob lp-bg__blob--a" />
      </div>

      {/* ============ NAVBAR ============ */}
      <header className={`lp-nav ${scrolled ? 'is-scrolled' : ''}`}>
        <div className="lp-nav__inner">
          <a href="#home" className="lp-nav__logo" onClick={scrollToId('home')}>
            <img src={studyDenLogo} alt="StudyDen" className="lp-nav__logo-img" />
          </a>

          <button
            className="lp-nav__menu-btn"
            onClick={toggleMobileMenu}
            aria-label="Toggle menu"
            aria-expanded={showMobileMenu}
          >
            <Icon.Menu />
          </button>

          <nav className={`lp-nav__links ${showMobileMenu ? 'is-open' : ''}`}>
            <a href="#home"            className="lp-nav__link" onClick={scrollToId('home')}>Home</a>
            <a href="#features"        className="lp-nav__link" onClick={scrollToId('features')}>Features</a>
            <a href="#pricing"         className="lp-nav__link" onClick={scrollToId('pricing')}>Pricing</a>
            <a href="#about"           className="lp-nav__link" onClick={scrollToId('about')}>About</a>
            <a href="#contact-section" className="lp-nav__link" onClick={scrollToId('contact-section')}>Contact</a>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="lp-nav__login"
            >
              Login
            </button>
            <button
              type="button"
              onClick={() => navigate('/signup')}
              className="lp-btn lp-btn--primary lp-nav__cta"
            >
              Get Started
            </button>
          </nav>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section className="lp-hero" id="home">
        <div className={`lp-hero__inner ${shouldFadeInHero ? 'is-in' : ''}`}>
          <div className="lp-hero__left">
            <span className="lp-hero__badge">
              <span className="lp-hero__badge-dot" /> AI-powered learning &amp; assessment
            </span>

            <h1 className="lp-hero__title">
              Learn. <span className="lp-hero__title-accent">Assess.</span> Progress.
            </h1>

            <p className="lp-hero__secondary">Everything learning, in one place.</p>

            <p className="lp-hero__subtitle">
              StudyDen brings together learning content, assessments, AI-powered marking and
              feedback, accessibility, and progress tracking in one intelligent platform built
              for educators and learners.
            </p>

            <div className="lp-hero__actions">
              <button type="button" className="lp-btn lp-btn--primary lp-btn--lg" onClick={() => navigate('/signup')}>
                Get Started <Icon.Arrow />
              </button>
              <button type="button" className="lp-btn lp-btn--ghost lp-btn--lg" onClick={goToContact}>
                Book a Demo
              </button>
            </div>

            <p className="lp-hero__educator"><Icon.Check /> Built by educators, for educators.</p>

            <div className="lp-hero__benefits" aria-label="StudyDen benefits">
              <span><Icon.Clock /> Save time</span>
              <span><Icon.Assessment /> Better insights</span>
              <span><Icon.Chart /> Greater progress</span>
            </div>
          </div>

          <div className="lp-hero__right">
            <HeroMockup />
          </div>
        </div>
      </section>

      {/* ============ FEATURES ============ */}
      <section className="lp-section lp-section--platform" id="features">
        <div className="lp-container">
          <div className="lp-section__head">
            <span className="lp-eyebrow">The StudyDen platform</span>
            <h2 className="lp-h2">One <span>connected</span> journey from learning to progress</h2>
            <p className="lp-lede">
              A complete learning environment with an intelligence layer that supports
              educators and learners at every stage.
            </p>
          </div>

          <div className="lp-feature-grid">
            {features.map((f, i) => (
              <div className="lp-feature" key={f.title} style={{ '--i': i }}>
                <div className="lp-feature__icon">{f.icon}</div>
                <h3 className="lp-feature__title">{f.title}</h3>
                <p className="lp-feature__text">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOW IT WORKS ============ */}
      <section className="lp-section lp-section--workflow">
        <div className="lp-container">
          <div className="lp-section__head">
            <span className="lp-eyebrow">Intelligent workflow</span>
            <h2 className="lp-h2">AI that supports educators at <span>every stage</span></h2>
            <p className="lp-lede">
              Create, assign, mark and understand performance with more clarity and less manual work.
            </p>
          </div>

            <div className="lp-steps">
              {[
              { n: '01', icon: <Icon.Document />, t: 'AI-assisted creation', d: 'Build relevant questions and assessments more efficiently with intelligent support.' },
              { n: '02', icon: <Icon.Assessment />, t: 'Assign with confidence', d: 'Share assessments with classes and apply accommodations such as additional time.' },
              { n: '03', icon: <Icon.Users />, t: 'Learn and complete', d: 'Learners access content and complete assessments in a clear, focused experience.' },
              { n: '04', icon: <Icon.Check />, t: 'AI-powered marking', d: 'Evaluate responses quickly and consistently against your criteria while you stay in control.' },
              { n: '05', icon: <Icon.Chart />, t: 'Feedback and insights', d: 'Turn results into meaningful feedback, reporting and performance insights.' },
            ].map((s) => (
              <div className={`lp-step ${s.n === '01' || s.n === '04' ? 'lp-step--ai' : ''}`} key={s.n}>
                <div className="lp-step__marker">
                  <div className="lp-step__icon" aria-hidden="true">{s.icon}</div>
                  <span className="lp-step__num">{s.n}</span>
                </div>
                <h4 className="lp-step__title">{s.t}</h4>
                <p className="lp-step__text">{s.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section className="lp-section lp-section--alt lp-section--testimonials" id="about">
        <div className="lp-container">
          <div className="lp-section__head">
            <span className="lp-eyebrow">Testimonials</span>
            <h2 className="lp-h2">What students &amp; teachers <span>say</span></h2>
            <p className="lp-testimonials__lede">
              Real feedback from the schools, teachers and students using StudyDen in their classrooms every day.
            </p>
          </div>

          <div className="lp-testimonial">
            <div className="lp-testimonial__quote-icon"><Icon.Quote /></div>
            <p className="lp-testimonial__quote">
              {testimonials[currentTestimonial]?.quote}
            </p>
            <div className="lp-testimonial__person">
              <div className="lp-testimonial__avatar">
                {testimonials[currentTestimonial]?.initial}
              </div>
              <div>
                <div className="lp-testimonial__name">{testimonials[currentTestimonial]?.name}</div>
                <div className="lp-testimonial__role">{testimonials[currentTestimonial]?.role}</div>
              </div>
            </div>

            <div className="lp-testimonial__controls">
              <button type="button" onClick={prevTestimonial} className="lp-icon-btn lp-icon-btn--prev" aria-label="Previous testimonial">‹</button>
              <div className="lp-testimonial__dots">
                {testimonials.map((_, i) => (
                  <button
                    type="button"
                    key={i}
                    aria-label={`Go to testimonial ${i + 1}`}
                    aria-current={i === currentTestimonial ? 'true' : undefined}
                    className={`lp-dot ${i === currentTestimonial ? 'is-active' : ''}`}
                    onClick={() => { setCurrentTestimonial(i); startAutoRotate(); }}
                  />
                ))}
              </div>
              <button type="button" onClick={nextTestimonial} className="lp-icon-btn lp-icon-btn--next" aria-label="Next testimonial">›</button>
            </div>
          </div>

          <div className="lp-stats">
            {stats.map((s, index) => (
              <div className="lp-stat" key={s.label}>
                <div className="lp-stat__icon" aria-hidden="true">
                  {[<Icon.Star />, <Icon.Users />, <Icon.Chart />][index]}
                </div>
                <div className="lp-stat__content">
                  <div className="lp-stat__value">{s.value}</div>
                  <div className="lp-stat__label">{s.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRICING ============ */}
      <section className="lp-section lp-section--pricing" id="pricing">
        <div className="lp-container lp-container--pricing">
          <div className="lp-section__head">
            <span className="lp-eyebrow">PRICING</span>
            <h2 className="lp-h2">Simple pricing for teachers and schools</h2>
            <p className="lp-lede">
              Start with a free trial, then choose a plan based on how your school uses StudyDen. No hidden fees, no complicated setup.
            </p>
          </div>

          <div className="lp-pricing-grid-new">
            {/* Free Trial */}
            <div className="lp-pricing-card">
              <div className="lp-pricing-card__header">
                <h3 className="lp-pricing-card__name">4 Week Free Trial</h3>
                <div className="lp-pricing-card__price">4 weeks free</div>
              </div>
              <p className="lp-pricing-card__description">Try StudyDen with no card required.</p>
              <ul className="lp-pricing-card__features">
                <li><span className="lp-check"><Icon.Check /></span> Full platform access</li>
                <li><span className="lp-check"><Icon.Check /></span> Starter credits included</li>
                <li><span className="lp-check"><Icon.Check /></span> AI test generation</li>
                <li><span className="lp-check"><Icon.Check /></span> AI marking and feedback</li>
                <li><span className="lp-check"><Icon.Check /></span> AutoReport access</li>
                <li><span className="lp-check"><Icon.Check /></span> Up to 2 classes</li>
              </ul>
              <button type="button" className="lp-btn lp-btn--primary lp-btn--block" onClick={() => navigate('/login')}>
                Start Free Trial
              </button>
            </div>

            {/* Individual Teacher */}
            <div className="lp-pricing-card">
              <div className="lp-pricing-card__header">
                <h3 className="lp-pricing-card__name">Individual Teacher</h3>
                <div className="lp-pricing-card__price">Contact for pricing</div>
              </div>
              <p className="lp-pricing-card__description">For teachers using StudyDen with their own classes.</p>
              <ul className="lp-pricing-card__features">
                <li><span className="lp-check"><Icon.Check /></span> AI marking and feedback</li>
                <li><span className="lp-check"><Icon.Check /></span> Test generation</li>
                <li><span className="lp-check"><Icon.Check /></span> PDF and scanned paper upload</li>
                <li><span className="lp-check"><Icon.Check /></span> Student tracking</li>
                <li><span className="lp-check"><Icon.Check /></span> AutoReport</li>
                <li><span className="lp-check"><Icon.Check /></span> Credit-based usage</li>
              </ul>
              <button type="button" className="lp-btn lp-btn--ghost lp-btn--block" onClick={() => navigate('/login')}>
                Get Started
              </button>
            </div>

            {/* Professional Plan - Featured */}
            <div className="lp-pricing-card lp-pricing-card--featured">
              <div className="lp-pricing-card__badge">Most Popular</div>
              <div className="lp-pricing-card__header">
                <h3 className="lp-pricing-card__name">Professional</h3>
                <div className="lp-pricing-card__price">Best for active teachers</div>
              </div>
              <p className="lp-pricing-card__description">For regular use across multiple classes.</p>
              <ul className="lp-pricing-card__features">
                <li><span className="lp-check"><Icon.Check /></span> More classes</li>
                <li><span className="lp-check"><Icon.Check /></span> Higher credit allowance</li>
                <li><span className="lp-check"><Icon.Check /></span> Priority processing</li>
                <li><span className="lp-check"><Icon.Check /></span> Advanced marking modes</li>
                <li><span className="lp-check"><Icon.Check /></span> OCR handwritten papers</li>
                <li><span className="lp-check"><Icon.Check /></span> Full analytics dashboard</li>
              </ul>
              <button type="button" className="lp-btn lp-btn--primary lp-btn--block" onClick={() => navigate('/login')}>
                Start Free Trial
              </button>
            </div>

            {/* School / Department Plan */}
            <div className="lp-pricing-card">
              <div className="lp-pricing-card__header">
                <h3 className="lp-pricing-card__name">School / Department</h3>
                <div className="lp-pricing-card__price">Custom</div>
              </div>
              <p className="lp-pricing-card__description">For departments, year groups and whole-school use.</p>
              <ul className="lp-pricing-card__features">
                <li><span className="lp-check"><Icon.Check /></span> Multiple teacher accounts</li>
                <li><span className="lp-check"><Icon.Check /></span> Shared student/class system</li>
                <li><span className="lp-check"><Icon.Check /></span> Department analytics</li>
                <li><span className="lp-check"><Icon.Check /></span> Central credit management</li>
                <li><span className="lp-check"><Icon.Check /></span> Custom onboarding</li>
                <li><span className="lp-check"><Icon.Check /></span> Priority support</li>
              </ul>
              <button type="button" className="lp-btn lp-btn--ghost lp-btn--block" onClick={goToContact}>
                Contact Sales
              </button>
            </div>
          </div>

          <div className="lp-pricing-trust">
            <Icon.Shield />
            <span>All plans include secure access and educator-first support.</span>
          </div>
        </div>
      </section>

      {/* ============ CONTACT / CTA ============ */}
      <section className="lp-section lp-section--cta" id="contact-section">
        <div className="lp-container">
          <div className="lp-cta">
            <span className="lp-cta__eyebrow">Get in touch</span>
            <h2 className="lp-cta__title">
              Want to bring <span>StudyDen</span> to your school?
            </h2>
            <p className="lp-cta__text">
              Email us at{' '}
              <a href="mailto:contact@aiautomarker.com">contact@aiautomarker.com</a>{' '}
              to request a demo or learn more about how StudyDen can support your students and teachers.
            </p>
            <div className="lp-cta__actions">
              <a href="mailto:contact@aiautomarker.com" className="lp-btn lp-btn--primary lp-btn--lg">
                Email us <Icon.Arrow />
              </a>
              <button type="button" className="lp-btn lp-btn--ghost lp-btn--lg" onClick={() => navigate('/login')}>
                Login
              </button>
            </div>
            <div className="lp-cta__benefits" aria-label="School support options">
              <span><Icon.Shield /> For schools</span>
              <span><Icon.Assessment /> Request a demo</span>
              <span><Icon.Check /> Tailored solutions</span>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="lp-footer">
        <div className="lp-container lp-footer__inner">
          <div className="lp-footer__brand">
            <img src={studyDenFooterLogo} alt="StudyDen" className="lp-footer__logo-img" />
          </div>
          <div className="lp-footer__meta">
            <p>© {new Date().getFullYear()} StudyDen. All rights reserved.</p>
            <p>contact@aiautomarker.com</p>
          </div>
          <div className="lp-footer__links">
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
            <a href="https://github.com" target="_blank" rel="noopener noreferrer">GitHub</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
